import './styles.css';
import { MailOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Checkbox, Form, Image, Input, Segmented, Typography, message } from 'antd';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import icon from '../../assets/images/icon.svg';
import { appConfig } from '../../config/appConfig';
import { createUser } from '../../services/api/user';
import { auth, database } from '../../services/firebase';
import { UserType } from '../../types/interfaces';

export const SignupPage = () => {
  const [messageApi, contextHolder] = message.useMessage();

  const [type, setType] = useState('PROFESSIONAL');
  const [loading, setLoading] = useState(false);

  const onFinish = async (values: {
    type: string;
    name: string;
    email: string;
    password: string;
    terms: boolean;
  }) => {
    if (!values.terms) {
      messageApi.open({
        type: 'error',
        content: 'Please accept the terms and conditions',
      });
      return;
    }

    try {
      setLoading(true);
      await createUser({
        name: values.name,
        email: values.email,
        password: values.password,
        type: values.type as UserType,
      });
      const user = await auth.signInWithEmailAndPassword(values.email, values.password);
      if (user.user?.uid) {
        await user.user.updateProfile({ displayName: values.name });
        await database.ref(`users/${user.user.uid}`).set({
          type: values.type,
          name: values.name,
          email: values.email,
          createdAt: new Date().toISOString(),
        });
      }
    } catch (error: any) {
      setLoading(false);
      messageApi.open({
        type: 'error',
        content: error.message,
      });
    }
  };

  return (
    <div className="signup">
      <div className="signup-form">
        <div className="signup-form-inner">
          {contextHolder}

          <Image width={40} src={icon} preview={false} />

          <Typography.Title style={{ marginBottom: 0, marginTop: 12, fontWeight: 'bold' }}>
            {appConfig.companyName}
          </Typography.Title>
          <Typography.Text>Unlock Your Full Potential 🌟</Typography.Text>

          <Form
            style={{ marginTop: 32 }}
            requiredMark={false}
            initialValues={{ type: 'PROFESSIONAL' }}
            layout="vertical"
            size="large"
            onFinish={onFinish}>
            <Form.Item
              style={{ marginBottom: 12 }}
              name="type"
              label="Join as"
              rules={[{ required: true, message: '' }]}>
              <Segmented
                block
                size="large"
                onChange={(e) => setType(e.toString())}
                options={[
                  { label: 'Professional', value: 'PROFESSIONAL' },
                  { label: 'Business', value: 'BUSINESS' },
                  { label: 'Hunter', value: 'HUNTER' },
                ]}
              />
            </Form.Item>
            <Form.Item
              style={{ marginBottom: 12 }}
              name="name"
              label={'Name'}
              rules={[{ required: true, message: '' }]}>
              <Input disabled={loading} prefix={<UserOutlined />} placeholder={'John Doe'} />
            </Form.Item>
            <Form.Item
              style={{ marginBottom: 12 }}
              name="email"
              label="Email"
              rules={[{ required: true, message: '' }]}>
              <Input disabled={loading} prefix={<MailOutlined />} placeholder="mail@example.com" />
            </Form.Item>

            <Form.Item
              style={{ marginBottom: 12 }}
              rules={[{ required: true, message: '' }]}
              name="terms"
              valuePropName="checked"
              initialValue={false}>
              <Checkbox>
                I have read and agree with the{' '}
                <a href={appConfig.termOfUseUrl} target="_blank" rel="noopener noreferrer">
                  Terms of Use
                </a>
                {' and '}
                <a href={appConfig.privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
              </Checkbox>
            </Form.Item>

            <Form.Item style={{ marginBottom: 4 }}>
              <Button block loading={loading} type="primary" htmlType="submit">
                Sign up as a{' '}
                {(() => {
                  switch (type) {
                    case 'PROFESSIONAL':
                      return 'Professional';
                    case 'BUSINESS':
                      return 'Business';
                    case 'HUNTER':
                      return 'Hunter';
                    default:
                      return 'Professional';
                  }
                })()}
              </Button>
            </Form.Item>

            <Form.Item style={{ textAlign: 'center' }}>
              Already have an account?{' '}
              <Link to="/login">
                <strong>Sign in!</strong>
              </Link>
            </Form.Item>
          </Form>
        </div>
      </div>
      <div className="signup-extra-content">
        <div className="signup-extra-content-container">
          <Typography.Title level={4} style={{ color: '#fff' }}>
            Join EnzRossi Connect and elevate your tech career. Collaborate with top talent and
            drive innovation forward!
          </Typography.Title>
          <br />
          <Typography.Text italic style={{ color: '#fff' }}>
            Welcome to the future of remote tech teams.
          </Typography.Text>
        </div>
      </div>
    </div>
  );
};
